/* ==========================================================================
   OEDEMA — popup.js
   Newsletter popup gating. Vanilla, self-contained, no external deps.
   NEVER shows on landing: honours a trigger (delay | scroll | exit_intent) and
   is frequency-capped + remembered via localStorage (oe_popup_count / oe_popup_done).
   Reuses window.oe.Overlay (focus trap, scrim, scroll lock, Esc) when present,
   else falls back to a minimal self-contained implementation.
   ========================================================================== */
(function () {
  'use strict';

  var KEY_COUNT = 'oe_popup_count';
  var KEY_DONE = 'oe_popup_done';

  function init() {
    var popup = document.querySelector('[data-popup]');
    if (!popup) return; // section absent — nothing to do

    var trigger = popup.getAttribute('data-trigger') || 'delay';
    var delay = parseInt(popup.getAttribute('data-delay'), 10);
    if (isNaN(delay)) delay = 6;
    var scrollDepth = parseInt(popup.getAttribute('data-scroll'), 10);
    if (isNaN(scrollDepth)) scrollDepth = 40;
    var maxShows = parseInt(popup.getAttribute('data-max-shows'), 10);
    if (isNaN(maxShows) || maxShows < 1) maxShows = 3;
    var remember = popup.getAttribute('data-remember') !== 'false';
    var forceSuccess = !!popup.querySelector('[data-popup-success]');

    /* --- persistence (guarded; degrades if localStorage unavailable) ------- */
    function readCount() {
      if (!remember) return 0;
      try {
        var c = parseInt(localStorage.getItem(KEY_COUNT) || '0', 10);
        return isNaN(c) ? 0 : c;
      } catch (e) { return 0; }
    }
    function isDone() {
      if (!remember) return false;
      try { return localStorage.getItem(KEY_DONE) === '1'; } catch (e) { return false; }
    }
    function saveCount(c) {
      if (!remember) return;
      try { localStorage.setItem(KEY_COUNT, String(c)); } catch (e) {}
    }
    function markDone() {
      if (!remember) return;
      try { localStorage.setItem(KEY_DONE, '1'); } catch (e) {}
    }

    /* --- overlay open/close (reuse shared Overlay, resolved lazily) -------- */
    /* window.oe is defined by theme.js which loads AFTER this script, so it must
       be read at call time, not captured at init (else the shared Overlay is missed). */
    var lastFocus = null;
    var trapHandler = null;
    var isOpen = false;

    function focusables() {
      return Array.prototype.slice.call(
        popup.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter(function (n) { return n.offsetParent !== null; });
    }

    function openFallback() {
      lastFocus = document.activeElement;
      var scrim = document.querySelector('[data-scrim]');
      if (scrim) { scrim.hidden = false; scrim.classList.add('is-active'); }
      document.body.style.overflow = 'hidden';
      popup.removeAttribute('hidden');
      popup.setAttribute('aria-hidden', 'false');
      popup.classList.add('is-open');
      trapHandler = function (e) {
        if (e.key === 'Escape') { closePopup(); return; }
        if (e.key !== 'Tab') return;
        var f = focusables(); if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      document.addEventListener('keydown', trapHandler);
      var f = focusables(); if (f.length) f[0].focus();
    }

    function closeFallback() {
      var scrim = document.querySelector('[data-scrim]');
      if (scrim) { scrim.classList.remove('is-active'); scrim.hidden = true; }
      document.body.style.overflow = '';
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      popup.setAttribute('hidden', '');
      if (trapHandler) { document.removeEventListener('keydown', trapHandler); trapHandler = null; }
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function openPopup() {
      if (isOpen) return;
      isOpen = true;
      var oe = window.oe;
      if (oe && oe.Overlay && oe.Overlay.open) { oe.Overlay.open(popup); }
      else { openFallback(); }
    }

    function closePopup() {
      if (!isOpen) return;
      isOpen = false;
      var oe = window.oe;
      if (oe && oe.Overlay && oe.Overlay.close) { oe.Overlay.close(); }
      else { closeFallback(); }
      teardownTriggers();
    }

    /* --- click-outside (backdrop) + explicit close/dismiss controls ------- */
    popup.addEventListener('click', function (e) {
      if (e.target === popup) { closePopup(); return; } // clicked scrim area, not panel
      var closer = e.target.closest('[data-popup-close], [data-popup-dismiss]');
      if (closer) { e.preventDefault(); closePopup(); }
    });

    // Full page POST on submit — record conversion before navigation.
    var form = popup.querySelector('form');
    if (form) {
      form.addEventListener('submit', function () { markDone(); });
    }

    /* --- gating ----------------------------------------------------------- */
    // Confirmation state after a successful signup: show once, don't count.
    if (forceSuccess) {
      markDone();
      openPopup();
      return;
    }

    if (isDone() || readCount() >= maxShows) return; // capped / converted — never show

    var shownThisPage = false;
    function showOnce() {
      if (shownThisPage) return;
      var count = readCount();
      if (isDone() || count >= maxShows) { teardownTriggers(); return; }
      shownThisPage = true;
      saveCount(count + 1);
      openPopup();
    }

    /* --- trigger wiring --------------------------------------------------- */
    var delayTimer = null;
    var scrollFn = null;
    var exitFn = null;

    function teardownTriggers() {
      if (delayTimer) { clearTimeout(delayTimer); delayTimer = null; }
      if (scrollFn) { window.removeEventListener('scroll', scrollFn); scrollFn = null; }
      if (exitFn) { document.removeEventListener('mouseout', exitFn); exitFn = null; }
    }

    if (trigger === 'scroll') {
      scrollFn = function () {
        var doc = document.documentElement;
        var scrollable = doc.scrollHeight - window.innerHeight;
        var pct = scrollable > 0 ? (window.pageYOffset / scrollable) * 100 : 100;
        if (pct >= scrollDepth) showOnce();
      };
      window.addEventListener('scroll', scrollFn, { passive: true });
      scrollFn();
    } else if (trigger === 'exit_intent') {
      exitFn = function (e) {
        if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) showOnce();
      };
      document.addEventListener('mouseout', exitFn);
    } else {
      // default: delay
      delayTimer = window.setTimeout(showOnce, Math.max(0, delay) * 1000);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
